import { FLOOR_ROOM_MAX_DB } from '@muzly/shared';

/**
 * roomNoise — "이 방은 측정에 쓸 수 없을 만큼 시끄럽다"는 판정. 순수 함수다.
 *
 * 근거는 음성 게이트의 바닥이다(`libs/shared/src/scoring/voice-gate.ts`). 게이트는
 * 300ms 창의 중앙값으로 이 방의 조용함을 재는데, 그 값이 `-40dBFS`보다 시끄러우면
 * 창을 버리고 다시 잰다. 그래서 **바닥이 오래도록 안 정해진다 = 조용한 창이 한 번도
 * 없었다**가 성립한다.
 *
 * 다만 바닥은 **사용자 목소리로도** 오염된다 — 시작하자마자 계속 소리를 내면 조용한
 * 창이 안 생긴다. 그래서 "엔진이 음을 한 번도 못 들었을 때"만 방을 탓한다. 목소리를
 * 듣고 있는 동안은 절대 이 화면을 띄우지 않는다: 잘 부르고 있는 사람의 측정을
 * 끊어 놓고 방 탓을 하는 것이 이 판정이 저지를 수 있는 가장 나쁜 실수다.
 *
 * React·시간 의존 없음 — vitest 대상.
 */

/**
 * 이 시간이 지나도 바닥이 안 잡히면 방을 의심한다(초).
 *
 * 게이트의 캘리브레이션 창이 300ms라 정상적인 방은 첫 창에서 바로 잡힌다. 4초면
 * 열 번 넘게 다시 잰 뒤라 "조용한 순간이 한 번도 없었다"가 우연이 아니다.
 */
export const ROOM_NOISE_AFTER_SEC = 4;

export interface RoomNoiseInput {
  /** 게이트가 이 방의 바닥을 정했나(`LiveSample.floorLocked`). */
  floorLocked: boolean;
  /** 이번 테이크에서 엔진이 실제로 들은 발성 길이(초). */
  voicedSec: number;
  /** 테이크가 시작된 뒤 흐른 벽시계 시간. */
  elapsedSec: number;
  /**
   * 이 테이크에 반주가 같이 나가나(노래 진단).
   *
   * 그러면 **방을 탓하지 않는다.** 판정의 전제는 "조용한 창이 한 번도 없었다면 방이
   * 시끄럽다"인데, 스피커로 반주를 틀어 놓은 테이크에서는 조용한 창이 없는 것이 정상이라
   * 전제가 통째로 무너진다. 실제로 iOS에서 반주가 들리게 되자마자 노래 진단이 시작 몇 초
   * 만에 소음 화면으로 끊겼다(2026-09-07).
   */
  backingTrack?: boolean;
}

/**
 * 전체화면 소음 안내(시안 `884:9858`)를 띄울지.
 *
 * 셋이 모두 참이어야 한다: 바닥이 안 잡혔고, 볼 만큼 기다렸고, 그동안 목소리는
 * 한 번도 안 들렸다. 반주가 나가는 테이크는 이 판정 자체를 건너뛴다(`backingTrack`).
 */
export const isRoomTooNoisy = ({
  floorLocked,
  voicedSec,
  elapsedSec,
  backingTrack = false,
}: RoomNoiseInput): boolean =>
  !backingTrack &&
  !floorLocked &&
  elapsedSec >= ROOM_NOISE_AFTER_SEC &&
  voicedSec === 0;

/**
 * 결과 화면의 소음 경고 임계(dBFS). 측정 테이크 바닥(`measuredRoomFloor`)이 이 값 이상이면 경고한다.
 *
 * 연구 데이터 측정 테이크(지속음·저음·고음 172개)의 방 바닥은 중앙값 -62, p90 -43이었다(2026-10-15,
 * `tools/pitch-replay` refs — 20ms 프레임 RMS 의 10번째 백분위). 게이트 바닥은 같은 입력의 RMS 지만
 * 300ms 창의 중앙값이라 조금 높게 나온다 — 그래서 기기 값으로는 p90 보다 경고가 잦을 수 있다(기기
 * `capture.noiseFloorDbfs` 분포로는 미검증). -45는 엔진 무음 게이트가 -45였던 시절이라면 이 방의
 * 소음 자체가 "소리 있음"으로 통과했을 높이다. 표시만 한다: 측정을 막지 않는다(ADR-0010 결정 5).
 */
export const MEASURED_NOISY_FLOOR_DBFS = -45;

/**
 * 측정 테이크(지속음·저음·고음)들의 게이트 바닥 중 가장 시끄러운 값.
 *
 * - `null`(테이크를 찍었는데 바닥이 끝내 안 잠김)은 **`FLOOR_ROOM_MAX_DB` 이상**으로 센다. 카운트다운
 *   3초를 포함해 조용한 300ms 창이 한 번도 없었다는 뜻이라 대개 경고가 겨냥하는 가장 시끄러운 방이다 —
 *   모름으로 건너뛰면 그 방들만 설문으로 빠진다. 드물게 아닌 경우(카운트다운 내내 소리를 냄, 입력이
 *   -80 아래로만 들어옴)는 경고가 잘못 뜨지만 표시뿐이라 그쪽을 택한다.
 * - `undefined`(그 테스트를 안 찍음)는 건너뛰고, 하나도 없으면 undefined(설문으로 폴백).
 *
 * 노래는 넣지 않는다 — 반주 에코 제거가 프레이즈 사이를 디지털 무음 근처로 눌러, 게이트가 그 창을
 * 버리거나(`FLOOR_ROOM_MIN_DB`) 반주 잔향을 방으로 잰다. 어느 쪽도 방이 아니다.
 */
export const measuredRoomFloor = (
  floors: readonly (number | null | undefined)[],
): number | undefined => {
  const known = floors
    .filter((f): f is number | null => f !== undefined)
    .map((f) => f ?? FLOOR_ROOM_MAX_DB);
  return known.length > 0 ? Math.max(...known) : undefined;
};

export const isMeasuredNoisy = (floorDbfs: number): boolean =>
  floorDbfs >= MEASURED_NOISY_FLOOR_DBFS;
